import { Box, BoxProps } from "@zenchef/styled-system/jsx";
import {
  aromaDraggableItem,
  AromaDraggableItemVariantProps,
} from "@zenchef/styled-system/recipes";
import React, { useContext, useRef } from "react";
import { Icon } from "../index";
import { DraggableContext } from "./DraggableContainer";
import { useSortable } from "@dnd-kit/sortable";
import Signifier from "./Signifier";
import { css, cx } from "@zenchef/styled-system/css";
import { useSignifierEdge } from "./useSignifierEdge";

export interface DraggableItemProps
  extends AromaDraggableItemVariantProps,
    Omit<BoxProps, "id"> {
  children: React.ReactNode;
  id: string | number;
  disabled?: boolean;
  forwardedRef?: (node: HTMLDivElement | null) => void;
}

export interface DraggableItemData {
  id: number | string;
  [key: string | symbol]: unknown;
}

const DraggableItem = ({
  children,
  disabled,
  id,
  forwardedRef,
  ...propsWithVariants
}: DraggableItemProps) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const context = useContext(DraggableContext);
  if (!context) {
    throw new Error("DraggableItem must be used within DraggableContainer");
  }
  const { flow } = context;

  const [variantProps, props] =
    aromaDraggableItem.splitVariantProps(propsWithVariants);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    active,
    over,
  } = useSortable({
    id,
    disabled,
  });

  const edge = useSignifierEdge(isOver, active, over, flow);

  const classes = aromaDraggableItem({
    ...variantProps,
    ...(isDragging ? { ghost: true } : {}),
  });

  return (
    <Box
      position="relative"
      display="flex"
      {...props}
      ref={(node) => {
        setNodeRef(node);
        if (forwardedRef) forwardedRef(node);
      }}
      {...attributes}
      {...listeners}
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      className={css({
        touchAction: "manipulation",
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition,
        cursor: disabled ? "not-allowed" : isDragging ? "grabbing" : "grab",
      })}
    >
      {isOver && edge ? <Signifier edge={edge} /> : null}
      <Box
        ref={itemRef}
        className={cx(classes.wrapper, "group", props.className)}
        data-disabled={disabled}
        data-draggable-id={id}
        draggable
        tabIndex={disabled ? -1 : 0}
      >
        <Box className={classes.knob} data-part="knob">
          <Icon name="drag-handle" />
        </Box>
        <Box className={classes.content}>{children}</Box>
      </Box>
    </Box>
  );
};

export default DraggableItem;
